import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/auth';
import { returnPathFrom } from '@/lib/return-to';

const REMEMBER_2FA_TOKEN_KEY = 'bridge_remember_2fa_token';

interface LocationState {
  sessionExpired?: boolean;
  from?: string;
}

interface Profile {
  id: number;
  email: string;
  firstname?: string | null;
  middlename?: string | null;
  lastname?: string | null;
}

interface LoginResponse {
  // Full auth response
  token?: string;
  user?: {
    id: number;
    email: string;
    name: string;
    roles: string[];
  };
  // 2FA verification required
  requires2fa?: boolean;
  pendingToken?: string;
  // 2FA setup required
  requires2faSetup?: boolean;
  setupToken?: string;
  // Profile info (always present)
  profile?: Profile;
}

interface ErrorResponse {
  error: string;
  message: string;
}

export function LoginForm(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, clearSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // FR-001f: the page ProtectedRoute sent the person here from, or '/'
  const returnTo = returnPathFrom(location.state);

  // T044k/FR-001e: Show session expired message
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.sessionExpired) {
      setInfo('Your session has expired. Please sign in again.');
      clearSessionExpired();
      // Clear the flag to prevent showing on refresh; keep where to return to
      void navigate(location.pathname, { replace: true, state: { from: state.from } });
    }
  }, [location, navigate, clearSessionExpired]);

  const getRemember2faToken = useCallback((): string | null => {
    try {
      return localStorage.getItem(REMEMBER_2FA_TOKEN_KEY);
    } catch {
      return null;
    }
  }, []);

  const handleSubmit = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setIsLoading(true);

    try {
      // Include remember token if available
      const remember2faToken = getRemember2faToken();

      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          remember2faToken: remember2faToken ?? undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as ErrorResponse;
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json() as LoginResponse;

      // Handle 2FA verification required
      if (data.requires2fa && data.pendingToken) {
        void navigate('/2fa/verify', {
          state: {
            pendingToken: data.pendingToken,
            profile: data.profile,
            from: returnTo,
          },
          replace: true,
        });
        return;
      }

      // Handle 2FA setup required
      if (data.requires2faSetup && data.setupToken) {
        void navigate('/2fa/setup', {
          state: {
            setupToken: data.setupToken,
            profile: data.profile,
            from: returnTo,
          },
          replace: true,
        });
        return;
      }

      // Full authentication
      if (data.token) {
        login(data.token);
        void navigate(returnTo, { replace: true });
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {info && (
        <Alert>
          <AlertDescription>{info}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="admin@scoutaround.io"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
          required
          disabled={isLoading}
          autoComplete="email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); }}
          required
          disabled={isLoading}
          autoComplete="current-password"
        />
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? 'Signing in...' : 'Sign in'}
      </Button>
    </form>
  );
}
